import { useState } from 'react'
import { aiService } from '../services/aiService'
import type { MedicationOrderContext, MedicationOrderInsight } from '../types/medicationAI'
import './MedicationAIInsightsPanel.css'

interface MedicationAIInsightsPanelProps {
  context: MedicationOrderContext
  disabled?: boolean
}

export function MedicationAIInsightsPanel({ context, disabled }: MedicationAIInsightsPanelProps) {
  const [insight, setInsight] = useState<MedicationOrderInsight | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const loadInsights = async () => {
    setLoading(true)
    setError(null)
    try {
      const result = await aiService.getMedicationOrderInsights(context)
      setInsight(result)
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : 'Failed to load AI insights'
      setError(msg)
      setInsight(null)
    } finally {
      setLoading(false)
    }
  }

  const hasContent =
    insight &&
    (insight.explanations.length > 0 ||
      insight.rankedAlternatives.length > 0 ||
      (insight.educationalNotes && insight.educationalNotes.length > 0))

  return (
    <div className="medication-ai-insights">
      <div className="medication-ai-insights-header">
        <h4>AI adjunct (non-authoritative)</h4>
        <button type="button" className="btn-secondary" onClick={() => void loadInsights()} disabled={disabled || loading}>
          {loading ? 'Loading…' : 'Get explanations & formulary ranking'}
        </button>
      </div>
      <p className="medication-ai-disclaimer" role="note">
        AI output is for education only and does not replace authoritative interaction, allergy, or dosing checks.
        Always rely on your clinical judgement and local protocols.
      </p>
      {error && (
        <div className="medication-ai-error" role="alert">
          {error}
        </div>
      )}
      {hasContent && insight && (
        <div className="medication-ai-body">
          {insight.explanations.length > 0 && (
            <section>
              <h5>Alert explanations</h5>
              <ul>
                {insight.explanations.map((ex) => (
                  <li key={ex.alertId}>
                    <strong>{ex.alertId}</strong>: {ex.briefSummary}
                    {ex.clinicalDetail && <p className="medication-ai-detail">{ex.clinicalDetail}</p>}
                  </li>
                ))}
              </ul>
            </section>
          )}
          {insight.rankedAlternatives.length > 0 && (
            <section>
              <h5>Formulary alternatives (ranked)</h5>
              <ul>
                {insight.rankedAlternatives.map((r) => (
                  <li key={r.referenceDrugId}>
                    <code>{r.referenceDrugId}</code> — {r.rationale}
                  </li>
                ))}
              </ul>
            </section>
          )}
          {insight.educationalNotes && (
            <section>
              <h5>Notes</h5>
              <p>{insight.educationalNotes}</p>
            </section>
          )}
        </div>
      )}
    </div>
  )
}
